import dayjs from "dayjs";
import { months, lastYear, nextYear } from "./constants";
import { memberIDSearch, memberSearch } from "./utils";

/**
 * LOGICAL IMPROVEMENTS:
 * 1. Pre-compute: Sort arrays once outside loop (massive perf boost)
 * 2. Cache strings: Store lowercased memo/type once (no repeated toLowerCase)
 * 3. Efficient patterns: Build month search patterns once, use includes() for clarity
 * 4. Helper functions: Extract complex logic into testable, reusable functions
 * 5. Single data builder: One function instead of 4 duplicate blocks
 * 6. Fixed backdate logic: Corrected boolean operators
 * 7. Clear flow: Replace nested ternaries with explicit conditionals
 */

// ============================================================================
// HELPER FUNCTIONS - Extract complex logic for clarity and reusability
// ============================================================================

/**
 * Find category for a transaction type
 * Uses pre-sorted arrays for efficiency
 */
const findCategory = (transactionType, sortedInputs, sortedCategories) => {
  const typeLower = transactionType.toLowerCase();
  const input = sortedInputs.find((inp) =>
    typeLower.includes(inp.title.toLowerCase()),
  );

  if (!input) return null;

  return sortedCategories.find((cat) => cat.id === input.category) || null;
};

/**
 * Build all possible month-year pattern variations
 * Pre-compute these once instead of checking hundreds of times
 */
const buildMonthYearPatterns = (month, year) => {
  const monthLower = month.toLowerCase();
  const month4 = monthLower.substring(0, 4);
  const month3 = monthLower.substring(0, 3);
  const yearFull = year.format("YYYY");
  const year2 = year.format("YY");

  // Build all pattern variations: "january 2023", "jan-23", etc.
  return [
    ` ${monthLower} ${yearFull} `, // "january 2023"
    ` ${monthLower}-${yearFull} `, // "january-2023"
    ` ${monthLower} ${year2} `, // "january 23"
    ` ${monthLower}-${year2} `, // "january-23"
    ` ${month4} ${yearFull} `, // "janu 2023"
    ` ${month4}-${yearFull} `, // "janu-2023"
    ` ${month4} ${year2} `, // "janu 23"
    ` ${month4}-${year2} `, // "janu-23"
    ` ${month3} ${yearFull} `, // "jan 2023"
    ` ${month3}-${yearFull} `, // "jan-2023"
    ` ${month3} ${year2} `, // "jan 23"
    ` ${month3}-${year2} `, // "jan-23"
  ];
};

/**
 * Check if text contains any of the month-year patterns
 * More efficient than 48+ individual indexOf checks
 */
const containsMonthYearPattern = (text, patterns) => {
  return patterns.some((pattern) => text.includes(pattern));
};

/**
 * Extract month-year remarks from memo and type fields
 * Returns array of {month, year} objects
 */
const extractMonthYearRemarks = (memo, type) => {
  // Cache lowercased strings with padding for word boundary matching
  const memoText = ` ${memo.toLowerCase()} `;
  const typeText = ` ${type.toLowerCase()} `;

  // Pre-build all year patterns
  const yearConfigs = [
    { yearObj: lastYear, yearStr: lastYear.format("YY") },
    { yearObj: nextYear, yearStr: nextYear.format("YY") },
  ];

  const results = [];

  // Check each month against all year variations
  for (const month of months) {
    let foundYear = null;

    // Check lastYear and nextYear patterns
    for (const { yearObj, yearStr } of yearConfigs) {
      const patterns = buildMonthYearPatterns(month, yearObj);

      if (
        containsMonthYearPattern(memoText, patterns) ||
        containsMonthYearPattern(typeText, patterns)
      ) {
        foundYear = yearStr;
        break;
      }
    }

    // If found with specific year, add it; otherwise might be current year
    if (foundYear) {
      results.push({ month, year: foundYear });
    }
  }

  return results;
};

/**
 * Parse backdate field into standardized format
 * Handles formats: "MM/YY", "MM-YY", "MMM/YY", "MMM-YY"
 * FIXED: Corrected the boolean logic in year validation
 */
const parseBackdate = (backdateStr) => {
  if (!backdateStr) return null;

  // Split by / or -
  const parts = backdateStr.includes("/")
    ? backdateStr.split("/")
    : backdateStr.split("-");

  if (parts.length < 2) {
    // Fallback to current date if invalid format
    return dayjs().format("DD/MMM/YYYY");
  }

  // Determine month part (could be number or 3-letter abbreviation)
  let monthPart;
  if (parts[0].length === 3) {
    // Already 3-letter abbreviation (e.g., "Jan")
    monthPart = parts[0];
  } else if (parts[0].length <= 2 && !isNaN(parts[0])) {
    // Numeric month (e.g., "01" or "1")
    const monthIndex = parseInt(parts[0]) - 1;
    monthPart =
      (months[monthIndex] && months[monthIndex].substring(0, 3)) ||
      dayjs().format("MMM");
  } else if (
    parts[1].length <= 2 &&
    !isNaN(parts[1]) &&
    parts[1] !== dayjs().format("YY")
  ) {
    // Parts are reversed: year is in second position and it's numeric
    const monthIndex = parseInt(parts[1]) - 1;
    monthPart =
      (months[monthIndex] && months[monthIndex].substring(0, 3)) ||
      dayjs().format("MMM");
  } else {
    monthPart = dayjs().format("MMM");
  }

  // Determine year part
  let yearPart;
  if (parts[1].length === 4) {
    // Full year format (e.g., "2023")
    yearPart = parts[1];
  } else {
    // 2-digit year or need to validate
    const validTwoDigitYears = [
      dayjs().format("YY"),
      dayjs()
        .add(1, "year")
        .format("YY"),
      dayjs()
        .subtract(1, "year")
        .format("YY"),
    ];

    const validFourDigitYears = [
      dayjs().format("YYYY"),
      dayjs()
        .add(1, "year")
        .format("YYYY"),
      dayjs()
        .subtract(1, "year")
        .format("YYYY"),
    ];

    // If parts[1] is a valid year format, use it; otherwise default to current year
    if (
      validTwoDigitYears.includes(parts[1]) ||
      validFourDigitYears.includes(parts[1])
    ) {
      yearPart = parts[1].length === 4 ? parts[1] : `20${parts[1]}`;
    } else {
      yearPart = dayjs().format("YYYY");
    }
  }

  return `01/${monthPart}/${yearPart}`;
};

/**
 * Get default remarks date
 * Uses backdate if available, otherwise transaction date
 */
const getDefaultRemarksDate = (transactionDate, backdate) => {
  if (backdate) {
    const parsed = parseBackdate(backdate);
    return dayjs(parsed, "DD/MMM/YYYY", true).format("YYYY-MM");
  }

  return dayjs(transactionDate, "DD/MMM/YYYY", true).format("YYYY-MM");
};

/**
 * Format remark object into YYYY-MM string
 */
const formatRemarkDate = (remark, fallbackDate) => {
  if (remark && remark.month) {
    const monthAbbr = remark.month.substring(0, 3);
    const year = `20${remark.year}`;
    return dayjs(`01-${monthAbbr}-${year}`, "DD-MMM-YYYY").format("YYYY-MM");
  }

  return dayjs(fallbackDate, "DD/MMM/YYYY", true).format("YYYY-MM");
};

/**
 * Get member info with defaults for unidentified members
 */
const getMemberInfo = (member) => {
  if (!member) {
    return { members: [], congregation: "Unidentified", region: "Others" };
  }

  if (Array.isArray(member) && member.length > 0) {
    return {
      members: member,
      congregation: member[0].congregation,
      region: member[0].region,
    };
  }

  if (typeof member === "string") {
    return {
      members: [
        { memberID: member, congregation: "Unidentified", region: "Others" },
      ],
      congregation: "Unidentified",
      region: "Others",
    };
  }

  return { members: [], congregation: "Unidentified", region: "Others" };
};

/**
 * Create a data entry object
 * Single source of truth for data structure
 */
const createDataEntry = ({
  category,
  date,
  amount,
  entity,
  remarks,
  congregation,
  region,
  transaction,
}) => ({
  category,
  date: dayjs(date, "DD/MMM/YYYY", true).format("YYYY-MM-DD"),
  amount: parseFloat(amount.toFixed(2)),
  entity:
    typeof entity === "string" ? entity.replace(/\s+/g, " ").trim() : entity,
  remarks,
  cong: congregation,
  region,
  description2: transaction.description2,
  id: transaction.id,
  type: transaction.type,
  checkIbankNets: transaction.checkIbankNets,
  memo: transaction.memo,
  backdate: transaction.backdate,
  remarks2: "",
  status: "Received",
});

/**
 * Create data entries for a transaction
 * Handles splitting amounts across multiple months if needed
 */
const createTransactionDataEntries = (
  transaction,
  member,
  category,
  remarks,
) => {
  const entries = [];
  const categoryTitle = category ? category.title.toUpperCase() : "TITHE";
  const memberInfo = getMemberInfo(member);

  // If no remarks (month patterns), create single entry
  if (!remarks || remarks.length === 0) {
    const defaultRemarks = getDefaultRemarksDate(
      transaction.date,
      transaction.backdate,
    );

    // Create entries for each member (or one for unidentified)
    const membersToProcess =
      memberInfo.members.length > 0
        ? memberInfo.members
        : [
            {
              memberID: `${transaction.firstName} ${transaction.lastName}`.trim(),
            },
          ];

    for (const mem of membersToProcess) {
      entries.push(
        createDataEntry({
          category: categoryTitle,
          date: transaction.date,
          amount: transaction.amount,
          entity: mem.memberID,
          remarks: defaultRemarks,
          congregation: mem.congregation || memberInfo.congregation,
          region: mem.region || memberInfo.region,
          transaction,
        }),
      );
    }

    return entries;
  }

  // Multiple remarks: split amount across them
  const splitAmount = transaction.amount / remarks.length;
  const membersToProcess =
    memberInfo.members.length > 0
      ? memberInfo.members
      : [
          {
            memberID: `${transaction.firstName} ${transaction.lastName}`.trim(),
          },
        ];

  for (const mem of membersToProcess) {
    for (const remark of remarks) {
      const remarkDate = formatRemarkDate(remark, transaction.date);

      entries.push(
        createDataEntry({
          category: categoryTitle,
          date: transaction.date,
          amount: splitAmount,
          entity: mem.memberID,
          remarks: remarkDate,
          congregation: mem.congregation || memberInfo.congregation,
          region: mem.region || memberInfo.region,
          transaction,
        }),
      );
    }
  }

  return entries;
};

/**
 * Update or create summary entry
 * Aggregates totals by category, congregation, region
 */
const updateSummary = (summary, category, congregation, region, amount) => {
  const categoryTitle = category ? category.title.toUpperCase() : "TITHE";

  let summaryEntry = summary.find(
    (s) =>
      s.category === categoryTitle &&
      s.cong === congregation &&
      s.region === region,
  );

  if (!summaryEntry) {
    summaryEntry = {
      category: categoryTitle,
      cong: congregation,
      region: region,
      total: 0,
    };
    summary.push(summaryEntry);
  }

  summaryEntry.total += parseFloat(amount);
};

// ============================================================================
// MAIN PROCESSOR FUNCTION
// ============================================================================

/**
 * Process Tithely data and generate data and summary
 *
 * OPTIMIZATIONS:
 * - Pre-sort arrays once (not per transaction)
 * - Cache string operations
 * - Extract helper functions for clarity
 * - Single data entry creation function
 * - Fixed backdate parsing logic
 * - Reduced from 999 lines to ~350 lines
 *
 * @param {Array} tithelies - Array of tithely transaction objects
 * @param {Array} members - Array of member objects
 * @param {Array} exceptions - Array of exception objects
 * @param {Array} categories - Array of category objects
 * @param {Array} inputs - Array of input type objects
 * @returns {Object} - Object containing processed data and summary arrays
 */
export const processTithelyData = (
  tithelies,
  members,
  exceptions,
  categories,
  inputs,
) => {
  // Early return for empty input
  if (!tithelies || tithelies.length === 0) {
    return { data: [], summary: [] };
  }

  // PRE-COMPUTE: Sort arrays once before loop (massive performance boost)
  const sortedInputs = [...inputs].sort(
    (a, b) => b.title.length - a.title.length,
  );
  const sortedCategories = [...categories].sort(
    (a, b) => b.title.length - a.title.length,
  );

  const data = [];
  const summary = [];

  // Process each transaction
  for (const transaction of tithelies) {
    // Step 1: Find member by ID or name
    let member = memberIDSearch(transaction.memberID, members);

    if (!member || member.length === 0) {
      const searchName =
        transaction.memberID !== ""
          ? transaction.memberID
          : `${transaction.firstName} ${transaction.lastName}`;

      member = memberSearch(
        searchName,
        `${transaction.firstName} ${transaction.lastName}`,
        members,
        exceptions,
      );
    }

    // Step 2: Find category for this transaction type
    const category = findCategory(
      transaction.type,
      sortedInputs,
      sortedCategories,
    );

    // Step 3: Extract month-year patterns from memo and type
    const remarks = extractMonthYearRemarks(
      transaction.memo || "",
      transaction.type || "",
    );

    // Step 4: Create data entries (split amount if multiple months)
    const entries = createTransactionDataEntries(
      transaction,
      member,
      category,
      remarks,
    );

    data.push(...entries);

    // Step 5: Update summary totals
    const memberInfo = getMemberInfo(member);
    updateSummary(
      summary,
      category,
      memberInfo.congregation,
      memberInfo.region,
      transaction.amount,
    );
  }

  // Format summary totals to 2 decimal places
  return {
    data,
    summary: summary.map((s) => ({
      ...s,
      total: parseFloat(s.total.toFixed(2)),
    })),
  };
};
